import { useEffect, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import StatCard from "@/components/StatCard";
import { currency, fmtDate } from "@/lib/format";
import { DollarSign, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PartnerCommissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/partners/me/commissions"); setItems(data); }
      catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const totals = {
    total: items.reduce((s, x) => s + (x.commission_amount || 0), 0),
    paid: items.filter((x) => x.status === "paid").reduce((s, x) => s + x.commission_amount, 0),
    pending: items.filter((x) => x.status !== "paid").reduce((s, x) => s + x.commission_amount, 0),
  };

  return (
    <div data-testid="commissions-page">
      <PageHeader eyebrow="Earnings" title="Commissions"
        subtitle="Every patient you've referred and the commission earned on each." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <StatCard label="Total earned" value={currency(totals.total)} icon={DollarSign} testid="stat-total" />
        <StatCard label="Paid out" value={currency(totals.paid)} icon={CheckCircle2} accent="#2A4D3E" testid="stat-paid" />
        <StatCard label="Pending" value={currency(totals.pending)} icon={Clock} accent="#B26B00" testid="stat-pending" />
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Patient</th>
                <th className="px-5 py-3">Treatment value</th>
                <th className="px-5 py-3">Rate</th>
                <th className="px-5 py-3">Commission</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2]" data-testid={`comm-${c.id}`}>
                  <td className="px-5 py-3.5 text-sm font-medium text-[#2A332C]">{c.patient_name}</td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{currency(c.treatment_value)}</td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{(c.commission_rate * 100).toFixed(0)}%</td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#2A4D3E]">{currency(c.commission_amount)}</td>
                  <td className="px-5 py-3.5"><StatusBadge kind="commission" value={c.status} /></td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-sm text-[#8E9992]">No commissions yet. Convert your first referral to start earning.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
